import { DateTime } from 'luxon';
import { AnalyticType } from './AnalyticType';
import { TimeLapse } from './TimeLapse';
import { Filter, FilterService } from './Filter';
import { getSplitByYear, getSplitByMonth, getSplitByWeekday, getSplitByHour } from '../assets/libs/broox/brooxCloudApi';
import { ChartDataset } from './ChartDataset';
import { ChartInput } from './ChartInput';
import { AccountParams } from './AccountParams';
import { ISaleService } from '../services/ISaleService';
import { SaleByTime } from './Sale';
import { toLocalDate } from './DateConversion';
import { resolveQueryWindow } from './QueryWindow';

interface HistoricalOption {
  type: AnalyticType,
  related: AnalyticType[]
}

const defaultOption = { type: AnalyticType.traffic, related: [ AnalyticType.views, AnalyticType.dwell, AnalyticType.attention, AnalyticType.gender, AnalyticType.age, AnalyticType.sales ] };

class HistoricalService {
  private getSplitBy = new Map<string, any>([[TimeLapse.years, getSplitByYear], [TimeLapse.months, getSplitByMonth], [TimeLapse.days, getSplitByWeekday], [TimeLapse.hours, getSplitByHour]]);
  private lineColors = new Map<string, string>([[AnalyticType.traffic, '#00E99B'], [AnalyticType.views, '#A162F7'], [AnalyticType.dwell, '#FF8744'], [AnalyticType.attention, '#1ACBE6']]);
  private barColors = new Map<string, any>([[AnalyticType.gender, { Female: '#00E99B', Male: '#1ACBE6', Unknown: '#E1E1E3' }], [AnalyticType.age, { 'Children (0-20)': '#EDE2FF', 'Young (21-40)': '#FFA16C', 'Middle Age (41-60)': '#1ACBE6', 'Elder (61-99)': '#A162F7', Unknown: '#E1E1E3' }]]);
  private saleService: ISaleService;

  constructor(saleService: ISaleService) {
    this.saleService = saleService;
  }

  static getAll(): HistoricalOption[] {
    return [
      defaultOption,
      { type: AnalyticType.views, related: [ AnalyticType.traffic, AnalyticType.dwell, AnalyticType.attention, AnalyticType.gender, AnalyticType.age, AnalyticType.sales ]},
      { type: AnalyticType.dwell, related: [ AnalyticType.traffic, AnalyticType.views, AnalyticType.attention, AnalyticType.sales ]},
      { type: AnalyticType.attention, related: [ AnalyticType.traffic, AnalyticType.views, AnalyticType.dwell, AnalyticType.sales ]},
      { type: AnalyticType.age, related: [ AnalyticType.traffic, AnalyticType.views ]},
      { type: AnalyticType.gender, related: [ AnalyticType.traffic, AnalyticType.views ]},
      { type: AnalyticType.sales, related: [ AnalyticType.traffic, AnalyticType.views, AnalyticType.dwell, AnalyticType.attention ]}
    ];
  }

  static getDefault(): HistoricalOption {
    return defaultOption;
  }

  async getChartInput(primaryOption: AnalyticType, secondaryOption: AnalyticType | undefined, timeLapse: TimeLapse, filter: Filter, params: AccountParams): Promise<ChartInput> {
    if(primaryOption === AnalyticType.gender || primaryOption === AnalyticType.age || secondaryOption === AnalyticType.gender || secondaryOption === AnalyticType.age) {
      return await this.getCompoundValueInput(primaryOption, secondaryOption, timeLapse, filter, params);
    }
    if(secondaryOption) {
      return await this.getDoubleValueInput(primaryOption, secondaryOption, timeLapse, filter, params);
    }
    else if(primaryOption === AnalyticType.sales) {
      return await this.getSalesChartInput(timeLapse, filter, params);
    }
    else {
      const splitBy = this.getSplitBy.get(timeLapse);
      const data = await splitBy(primaryOption, filter, params.host, params.token || params.id, params.apiKey);
      return this.getSingleValueInput(data[primaryOption], this.lineColors.get(primaryOption) || '#00E99B');
    }
  }

  private getFilter(filter: Filter, option?: AnalyticType): any {
    const f: any = { ...filter, extra: {} };
    if(option === AnalyticType.traffic) {
      f.extra.ev_action = 'out';
      f.includeUnknown = true;
    }
    if(option === AnalyticType.views) {
      f.extra.is_view = true;
      f.includeUnknown = true;
    }
    return f;
  }

  private getDoubleValueInput = async (primaryOption: AnalyticType, secondaryOption: AnalyticType, timeLapse: TimeLapse, filter: Filter, params: AccountParams): Promise<ChartInput> => {
    const primaryInput = await this.getChartInputByOption(primaryOption, timeLapse, filter, params);
    const secondaryInput = await this.getChartInputByOption(secondaryOption, timeLapse, filter, params);
    primaryInput.datasets.push(...secondaryInput.datasets);
    return primaryInput;
  }

  private async getSalesChartInput(timeLapse: TimeLapse, filter: Filter, params: AccountParams): Promise<ChartInput> {
    const salesColor = '#28c76f';
    // Ventana semiabierta (CX-638 §05/Fase 6): '<' contra un toMs exclusivo.
    const range = { start: toLocalDate(filter.from), end: toLocalDate(filter.to) };
    const { fromMs, toMs } = resolveQueryWindow(range, filter.cutoff ?? null);
    const conditions: { field: string, operator: string, value: any }[] = [ { field: 'timestamp', operator: '>=', value: fromMs }, { field: 'timestamp', operator: '<', value: toMs } ];
    const locationCondition = FilterService.getLocationCondition(filter);
    locationCondition && conditions.push(locationCondition);
    const sales = await this.saleService.byTimeAndWhere(conditions);
    let data = new Map<number, number>();
    if(timeLapse === TimeLapse.years) {
      data = this.getValues(sales, 'year', []);
    }
    else if(timeLapse === TimeLapse.months) {
      const keys = [ 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December' ];
      data = this.getValues(sales, 'monthLong', keys);
    }
    else if(timeLapse === TimeLapse.days) {
      const keys = [ 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday' ];
      data = this.getValues(sales, 'weekdayLong', keys);
    }
    else if(timeLapse === TimeLapse.hours) {
      const keys = [];
      for(let i = 0; i < 24; i++) {
        keys.push(i);
      }
      data = this.getValues(sales, 'hour', keys);
    }
    const dataset: ChartDataset = { data: Array.from(data.values()), color: salesColor, fill: 'start', label: '', backgroundColor: [] };
    const labels = Array.from(data.keys()).map(k => k ? k.toString() : '');
    return { datasets: [ dataset ], labels: labels };
  }

  private getValues(sales: SaleByTime[], property: string, keys: any[]): Map<number, number> {
    const data = new Map<any, number>();
    for(const key of keys) {
      data.set(key, 0);
    }
    for(const sale of sales) {
      let key = sale.time;
      if(property !== 'hour') {
        const date = DateTime.fromMillis(sale.timestamp).setLocale('en-US');
        key = date[property];
      }
      let value = data.get(key);
      if(!value) {
        value = 0;
      }
      data.set(key, value + sale.amount);
    }
    return data;
  }

  private getSingleValueInput = (data: any, color: string): ChartInput => {
    const dataset: ChartDataset = { data: [], color: color, fill: 'start', label: '', backgroundColor: [] };
    const labels: string[] = [];
    for(let item of data) {
      dataset.data.push(item.value);
      labels.push(item.label);
    }
    return { datasets: [ dataset ], labels: labels };
  }

  private getCompoundValueInput = async (primaryOption: AnalyticType, secondaryOption: AnalyticType | undefined, timeLapse: TimeLapse, filter: Filter, params: AccountParams) => {
    let barOption = primaryOption;
    let option = secondaryOption;
    if(secondaryOption === AnalyticType.age || secondaryOption === AnalyticType.gender) {
      barOption = secondaryOption;
      option = primaryOption;
    }
    const f = this.getFilter(filter, option);
    const splitBy = this.getSplitBy.get(timeLapse);
    const data = await splitBy(barOption, f, params.host, params.token || params.id, params.apiKey);
    return this.getGroupValueInput(data[barOption], this.barColors.get(barOption) || {});
  }

  private getGroupValueInput = (data: any, colors: any): ChartInput => {
    const datasets = new Map<string, ChartDataset>();
    const labels: string[] = [];
    for(let item of data) {
      labels.push(item.label);
      const total = item.values.reduce((accumulator: number, current: any) => accumulator + current.value, 0);
      for(let d of item.values) {
        let dataset = datasets.get(d.label);
        if(!dataset) {
          dataset = { data: [], label: d.label, backgroundColor: colors[d.label] || '#000000' };
          datasets.set(d.label, dataset);
        }
        dataset.data.push((d.value / total * 100) as any);
      }
    }
    return { datasets: Array.from(datasets.values()), labels: labels };
  }

  private getChartInputByOption = async (option: AnalyticType, timeLapse: TimeLapse, filter: Filter, params: AccountParams): Promise<ChartInput> => {
    const splitBy = this.getSplitBy.get(timeLapse);
    if(option === AnalyticType.sales) {
      return await this.getSalesChartInput(timeLapse, filter, params);
    }
    else {
      const data = await splitBy(option, filter, params.host, params.token || params.id, params.apiKey);
      return this.getSingleValueInput(data[option], this.lineColors.get(option) || '#00E99B');
    }
  }
}

export { HistoricalOption, HistoricalService };
